import React, { lazy, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router-dom'

import { AppWellness } from 'pages/AppWellness'
import { CheckInbox } from 'pages/CheckInbox'
import { DownloadLong } from 'pages/Download'
import { DownloadWellness } from 'pages/DownloadWellness'

import { PageShell } from 'components/PageShell'
import { PreloadPageWrapper } from 'components/PreloadPageWrapper'
import { ProtectedRoute } from 'components/ProtectedRoute'
import { SubscribeFlow } from 'components/SubscribeFlow'

import {
  selectCurrentVariantCohort,
  selectLanguage,
} from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'

import { useActiveSteps } from 'hooks/useActiveSteps'
import { useAvailableUpsellPages } from 'hooks/useAvailableUpsellPages'
import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { usePageConfig } from 'hooks/usePageConfig'
import { usePostPaymentOrder } from 'hooks/usePostPaymentOrder'
import { useUserStatus } from 'hooks/useUserStatus'

import { getPathFromPageId } from 'helpers/getPathFromPageId'

import { Account, SignUp } from 'modules/registration/pages'

import { TIdToPageMap } from 'models/common.model'
import { IStep } from 'models/variant.model'

import { ID_TO_PAGE_MAP, PageId } from 'page-constants'

const PaymentProvider = lazy(() => import('components/PaymentProvider'))
const PrimerProvider = lazy(() =>
  import('providers/PrimerProvider').then((m) => ({
    default: m.PrimerProvider,
  })),
)

export const RouteList: React.FC = () => {
  const availableRoute = useUserStatus()
  const { search, hash } = useLocation()
  const { isHeaderOverlay } = usePageConfig()
  const { isFirstQuestionPage, hasHeader } = useGetPageInfo()
  const { isAccountBeforeUpsellOrder } = usePostPaymentOrder()
  const availableUpsellPages = useAvailableUpsellPages()

  const steps = useActiveSteps()
  const cohort = useSelector(selectCurrentVariantCohort)
  const uuid = useSelector(selectUUID)
  const language = useSelector(selectLanguage)

  const isUpsellRouteAvailable = (pageId: string) => {
    const isUnvisitedUpsellRoute = availableUpsellPages.includes(pageId)

    if (!isUnvisitedUpsellRoute) {
      return false
    }

    if (!isAccountBeforeUpsellOrder) {
      return true
    }

    return availableRoute !== PageId.ACCOUNT && availableRoute !== PageId.SIGNUP
  }

  const firstPagePath = useMemo(
    () =>
      getPathFromPageId({
        pageId: steps[0].id,
        cohort,
        uuid,
        language,
        currentSearch: search,
        hash,
      }),
    [steps, cohort, uuid, language, search, hash],
  )

  const { onboardingPages, subscriptionPages, upsellPages } = useMemo(() => {
    return steps.reduce<{
      onboardingPages: IStep[]
      subscriptionPages: IStep[]
      upsellPages: IStep[]
    }>(
      (accum, { isSubscriptions, isPayment, isUpsell }, ind, arr) => {
        if (isSubscriptions || isPayment) {
          accum.subscriptionPages.push(arr[ind])
          return accum
        }

        if (isUpsell) {
          accum.upsellPages.push(arr[ind])
          return accum
        }

        accum.onboardingPages.push(arr[ind])
        return accum
      },
      {
        onboardingPages: [],
        subscriptionPages: [],
        upsellPages: [],
      },
    )
  }, [steps])

  const nextPageByPageId = useMemo(
    () =>
      steps.reduce<TIdToPageMap>((accum, { id }, index) => {
        const nextStep = steps[index + 1]

        if (nextStep) {
          accum[id] = ID_TO_PAGE_MAP[nextStep.id]
        }

        return accum
      }, {}),
    [steps],
  )

  return useRoutes([
    { index: true, element: <Navigate to={firstPagePath} /> },
    {
      element: <SubscribeFlow />,
      children: onboardingPages.map(({ id: pageId }, index, arr) => {
        const CurrentPage = ID_TO_PAGE_MAP[pageId]
        const NextPage = nextPageByPageId[pageId]

        const nextPagePath = arr[index + 1]
          ? getPathFromPageId({
              pageId: arr[index + 1].id,
              cohort,
              uuid,
              language,
              currentSearch: search,
              hash,
            })
          : ''

        const alternativePageId = arr.find(
          (dynamicPage, dynamicPageIndex) =>
            !dynamicPage.isSkippable && dynamicPageIndex > index,
        )?.id

        const alternativePagePath = getPathFromPageId({
          pageId: alternativePageId || arr[index + 1]?.id,
          cohort,
          uuid,
          language,
          currentSearch: search,
          hash,
        })

        return {
          path: pageId,
          element: (
            <ProtectedRoute isAvailable={!availableRoute}>
              <PreloadPageWrapper nextPage={NextPage}>
                <PageShell
                  isHeaderOverlay={isHeaderOverlay}
                  hasHeader={hasHeader}
                >
                  <CurrentPage
                    pageId={pageId}
                    nextPagePath={nextPagePath}
                    isFirstPage={isFirstQuestionPage}
                    alternativePagePath={alternativePagePath}
                  />
                </PageShell>
              </PreloadPageWrapper>
            </ProtectedRoute>
          ),
        }
      }),
    },
    {
      element: (
        <PaymentProvider>
          <Outlet />
        </PaymentProvider>
      ),
      children: [
        {
          element: (
            <PrimerProvider>
              <SubscribeFlow />
            </PrimerProvider>
          ),
          children: subscriptionPages.map(({ id: pageId }, index, arr) => {
            const PurchasePage = ID_TO_PAGE_MAP[pageId]
            const NextPage = nextPageByPageId[pageId]
            const nextPagePath = arr[index + 1]
              ? getPathFromPageId({
                  pageId: arr[index + 1].id,
                  cohort,
                  uuid,
                  language,
                  currentSearch: search,
                  hash,
                })
              : ''

            return {
              path: pageId,
              element: (
                <ProtectedRoute isAvailable={!availableRoute}>
                  <PreloadPageWrapper nextPage={NextPage}>
                    <PageShell
                      isAnimation={false}
                      isHeaderOverlay={isHeaderOverlay}
                      hasHeader={hasHeader}
                    >
                      <PurchasePage
                        pageId={pageId}
                        nextPagePath={nextPagePath}
                        alternativePagePath="/"
                      />
                    </PageShell>
                  </PreloadPageWrapper>
                </ProtectedRoute>
              ),
            }
          }),
        },
        {
          element: <SubscribeFlow />,
          children: upsellPages.map(({ id: pageId }, index, arr) => {
            const UpsellPage = ID_TO_PAGE_MAP[pageId]
            const NextPage = nextPageByPageId[pageId]
            const nextPagePath = getPathFromPageId({
              pageId: arr[index + 1]?.id,
              cohort,
              uuid,
              language,
              currentSearch: search,
              hash,
            })

            return {
              path: pageId,
              element: (
                <ProtectedRoute isAvailable={isUpsellRouteAvailable(pageId)}>
                  <PreloadPageWrapper nextPage={NextPage}>
                    <PageShell
                      isHeaderOverlay={isHeaderOverlay}
                      hasHeader={hasHeader}
                    >
                      <UpsellPage
                        pageId={pageId}
                        nextPagePath={nextPagePath}
                        alternativePagePath="/"
                      />
                    </PageShell>
                  </PreloadPageWrapper>
                </ProtectedRoute>
              ),
            }
          }),
        },
      ],
    },
    {
      path: PageId.CHECK_INBOX,
      element: (
        <ProtectedRoute isAvailable={!availableRoute}>
          <PageShell isHeaderOverlay={isHeaderOverlay} hasHeader={hasHeader}>
            <CheckInbox />
          </PageShell>
        </ProtectedRoute>
      ),
    },
    {
      path: PageId.SIGNUP,
      element: (
        <ProtectedRoute
          isAvailable={
            availableRoute === PageId.SIGNUP ||
            (!isAccountBeforeUpsellOrder &&
              !!availableUpsellPages?.includes(availableRoute))
          }
        >
          <PageShell isHeaderOverlay={isHeaderOverlay} hasHeader={hasHeader}>
            <SignUp />
          </PageShell>
        </ProtectedRoute>
      ),
    },
    {
      path: PageId.ACCOUNT,
      element: (
        <ProtectedRoute
          isAvailable={
            availableRoute === PageId.ACCOUNT ||
            (!isAccountBeforeUpsellOrder &&
              !!availableUpsellPages?.includes(availableRoute))
          }
        >
          <PageShell isHeaderOverlay={isHeaderOverlay} hasHeader={hasHeader}>
            <Account />
          </PageShell>
        </ProtectedRoute>
      ),
    },
    {
      path: PageId.DOWNLOAD,
      element: (
        <ProtectedRoute isAvailable={availableRoute === PageId.DOWNLOAD}>
          <PageShell isHeaderOverlay={isHeaderOverlay} hasHeader={hasHeader}>
            <DownloadLong />
          </PageShell>
        </ProtectedRoute>
      ),
    },
    {
      path: PageId.DOWNLOAD_WELLNESS,
      element: (
        <ProtectedRoute
          isAvailable={availableRoute === PageId.DOWNLOAD_WELLNESS}
        >
          <PageShell isHeaderOverlay={isHeaderOverlay} hasHeader={hasHeader}>
            <DownloadWellness
              pageId={PageId.DOWNLOAD_WELLNESS}
              nextPagePath=""
              alternativePagePath=""
            />
          </PageShell>
        </ProtectedRoute>
      ),
    },
    {
      path: PageId.APP_WELLNESS,
      element: (
        <ProtectedRoute isAvailable={availableRoute === PageId.APP_WELLNESS}>
          <PageShell isHeaderOverlay={isHeaderOverlay} hasHeader={hasHeader}>
            <AppWellness />
          </PageShell>
        </ProtectedRoute>
      ),
    },

    { path: '*', element: <Navigate to={firstPagePath} /> },
  ])
}
